import update from 'immutability-helper';
import { cloneDeep, isEmpty } from 'lodash';
import { v4 as uuidV4 } from 'uuid';
import I18n from '../locale';
import cinemasAPI from '../common/client/cinemasSearch';
import {
  filterDuplicateDevices,
  handleDeviceMovement,
  cleanDeviceForPostMessage,
  modifyIpForDevice,
  removeDeviceFromScreen,
  collectIpUuidsFromDevice,
} from '../components/devices/utils/actionHelpers';
import { displayError } from './app';

const initialState = {
  data: {
    standby_devices: [],
    auditoriums: [],
    ip_details: [],
  },
  loading: false,
  error: false,
  deviceModels: [],
  pageParams: {},
};

/* -- PostMessageAPI for iframe -- */
const sendPostMessageToParentFrame = (data) => {
  const dataToSend = cloneDeep(data);

  // Process auditoriums to clean devices and reconstruct ip_details
  const cleanedAuditoriums = dataToSend.auditoriums.map((auditorium) => {
    // Build IP assignment map using flatMap for cleaner code
    const ipAssignmentMap = new Map(
      (auditorium.devices || []).flatMap((device) => {
        const assignments = [];
        // Handle unmapped devices
        if (device.ip_uuid && (!device.suites || device.suites.length === 0)) {
          assignments.push([
            device.ip_uuid,
            {
              device_id: device.uuid,
              suite_number: null,
            },
          ]);
        }
        // Handle devices with suites
        (device.suites || []).forEach((suite) => {
          if (suite.ip_uuid) {
            assignments.push([
              suite.ip_uuid,
              {
                device_id: device.uuid,
                suite_number: suite.suite_number,
              },
            ]);
          }
        });
        return assignments;
      }),
    );

    // Reconstruct ip_details using the map
    const finalIpDetails = (auditorium.ip_details || []).map((ip) => ({
      uuid: ip.uuid,
      device_ip: ip.device_ip,
      subnet_mask: ip.subnet_mask,
      gateway: ip.gateway,
      device_id: ipAssignmentMap.get(ip.uuid)?.device_id || null,
      suite_number: ipAssignmentMap.get(ip.uuid)?.suite_number || null,
    }));

    // Clean devices using the helper function
    const cleanedDevices = (auditorium.devices || [])
      .map(cleanDeviceForPostMessage)
      .filter(Boolean);

    // Construct final auditorium object
    const {
      devices: _devices,
      ip_details: _ip_details,
      screenId: _screenId,
      ...restAuditorium
    } = auditorium;
    return {
      ...restAuditorium,
      devices: cleanedDevices,
      ip_details: finalIpDetails,
    };
  });

  // Clean standby devices
  const cleanedStandbyDevices =
    dataToSend.standby_devices
      ?.map(cleanDeviceForPostMessage)
      .filter(Boolean) || [];

  // Construct final payload
  const {
    ip_details: _topLevelIpDetails,
    auditoriums: _originalAuditoriums,
    standby_devices: _originalStandby,
    ...restDataToSend
  } = dataToSend;

  const payload = {
    ...restDataToSend,
    auditoriums: cleanedAuditoriums,
    standby_devices: cleanedStandbyDevices,
  };

  window.parent.postMessage({ type: 'UPDATED_FACILITY_DEVICES', payload }, '*');
};

/* -- Constants -- */
const SET_DEVICES = 'facilityDevices/SET_DEVICES';
const SET_LOADING = 'facilityDevices/SET_LOADING';
const SET_ERROR = 'facilityDevices/SET_ERROR';
const SET_DEVICE_MODELS = 'facilityDevices/SET_DEVICE_MODELS';
const SET_PAGE_PARAMS = 'facilityDevices/SET_PAGE_PARAMS';

/* -- Actions -- */
export const setPageParams = (payload) => ({
  type: SET_PAGE_PARAMS,
  payload,
});

const setLoading = (bool) => ({
  type: SET_LOADING,
  payload: bool,
});

const setDevices = (payload) => ({
  type: SET_DEVICES,
  payload,
});

const setDeviceError = () => ({
  type: SET_ERROR,
});

const setDeviceModels = (data) => ({
  type: SET_DEVICE_MODELS,
  payload: data,
});

/* -- API Calls -- */
export function fetchDeviceByFacilityId(id) {
  return (dispatch, getState) => {
    if (!getState().theatres.loading) {
      dispatch(setLoading(true));
      cinemasAPI
        .get(`/facilities/${id}/devices`)
        .then((response) => {
          const auditoriums = response.data.auditoriums
            .map((auditorium) => ({
              ...auditorium,
              screenId: auditorium.uuid,
            }))
            .sort((a, b) => Number(a.number) - Number(b.number));
          const result = { ...response.data, auditoriums };
          dispatch(setDevices(result));
          dispatch(setLoading(false));
        })
        .catch(() => {
          dispatch(displayError(I18n.t('errorFetchingDevices')));
          dispatch(setDeviceError());
        });
    }
  };
}

export function fetchDeviceModels() {
  return (dispatch) => {
    cinemasAPI
      .get(`/device_models`)
      .then((response) => {
        const result = response.data
          .map((item) => {
            const {
              name,
              id,
              uuid,
              is_dci: dci,
              translations,
              device_type: code,
              ...rest
            } = item;
            return {
              id,
              name,
              value: uuid,
              label: name,
              translations,
              data: {
                ...rest,
                device_model_id: id,
                code,
                model_id: uuid,
                specification: dci ? 'DCI' : 'Non DCI',
              },
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        dispatch(setDeviceModels(result));
      })
      .catch(() => {
        dispatch(displayError(I18n.t('errors.fetchDeviceModels')));
      });
  };
}

export function fetchDeviceRoles(deviceModel) {
  return async (dispatch) => {
    return cinemasAPI
      .get(`/device_models/${deviceModel}/device_roles`)
      .then((response) => {
        return (
          response.data.device_roles?.map((item) => ({
            ...item,
            value: `${item.name} - ${item.description}`,
          })) || []
        );
      })
      .catch(() => {
        dispatch(displayError(I18n.t('errors.fetchDeviceRoles')));
      });
  };
}

export function createDevice({ devices, screenId }) {
  return (dispatch, getState) => {
    const facilityDevicesData = getState().facilityDevices.data;
    const payload = cloneDeep(facilityDevicesData);

    const newDevices = devices.filter(Boolean);
    if (newDevices.length === 0) return;

    let screenIndex = -1;
    if (screenId) {
      screenIndex = payload.auditoriums.findIndex(
        (i) => i.screenId === screenId,
      );
      if (screenIndex === -1) {
        console.error(`[createDevice] Target screen ${screenId} not found.`);
        return;
      }

      const targetAuditorium = payload.auditoriums[screenIndex];

      // Filter out devices from standby that match the new devices being added
      payload.standby_devices = filterDuplicateDevices(
        payload.standby_devices || [],
        newDevices,
      );

      // Get the current devices on the screen
      const existingScreenDevices = targetAuditorium.devices || [];
      const updatedDevices = [...existingScreenDevices];

      newDevices.forEach((device) => {
        const newSuiteNumber = device.suites?.[0]?.suite_number;
        // 1. If an unmapped device with same serial/model exists, update it to be in the suite
        const unmappedIdx = updatedDevices.findIndex(
          (d) =>
            d.device_model_id === device.device_model_id &&
            d.serial_number === device.serial_number &&
            (!d.suites || d.suites.length === 0),
        );
        if (unmappedIdx !== -1) {
          // Update the unmapped device to become a suite device
          const unmappedDevice = { ...updatedDevices[unmappedIdx] };
          // Add the suite from the new device
          const newSuite = (device.suites && device.suites[0]) || null;
          if (newSuite) {
            unmappedDevice.suites = [newSuite];
            unmappedDevice.is_unmapped = false;
            // Assign IP if provided
            if (device.ip_uuid) {
              const { device: updatedDevice, screen: updatedScreen } =
                modifyIpForDevice({
                  device: unmappedDevice,
                  ipUuid: device.ip_uuid,
                  ipDetails: {
                    device_ip: device.device_ip,
                    subnet_mask: device.subnet_mask,
                    gateway: device.gateway,
                  },
                  screen: targetAuditorium,
                  suiteNumber: newSuite.suite_number,
                });
              updatedDevices[unmappedIdx] = updatedDevice;
              Object.assign(targetAuditorium, updatedScreen);
            } else {
              updatedDevices[unmappedIdx] = unmappedDevice;
            }
          }
        } else {
          // 2. If a device with same serial/model AND same suite exists, skip
          const alreadyInSameSuite = updatedDevices.some(
            (d) =>
              d.device_model_id === device.device_model_id &&
              d.serial_number === device.serial_number &&
              d.suites &&
              d.suites.some(
                (s) => String(s.suite_number) === String(newSuiteNumber),
              ),
          );
          if (alreadyInSameSuite) {
            return; // Silent skip
          }
          // 3. Otherwise, add as new device (for a different suite)
          if (device.ip_uuid) {
            const { device: updatedDevice, screen: updatedScreen } =
              modifyIpForDevice({
                device,
                ipUuid: device.ip_uuid,
                ipDetails: {
                  device_ip: device.device_ip,
                  subnet_mask: device.subnet_mask,
                  gateway: device.gateway,
                },
                screen: targetAuditorium,
                suiteNumber: device.suites?.[0]?.suite_number,
              });
            updatedDevices.push(updatedDevice);
            Object.assign(targetAuditorium, updatedScreen);
          } else {
            updatedDevices.push(device);
          }
        }
      });

      // Replace devices list with updated one
      targetAuditorium.devices = updatedDevices;
      payload.auditoriums[screenIndex] = targetAuditorium;
    } else {
      // Adding to standby - filter out duplicates already in standby
      const existingStandbySerials = new Set(
        (payload.standby_devices || []).map(
          (d) => `${d.device_model_id}_${d.serial_number}`,
        ),
      );
      // For each new device, if a match exists in auditoriums, copy software_version and is_certificate_locked
      const uniqueNewDevices = newDevices
        .filter(
          (newDevice) =>
            !existingStandbySerials.has(
              `${newDevice.device_model_id}_${newDevice.serial_number}`,
            ),
        )
        .map((newDevice) => {
          // Search auditoriums for a device with same serial_number and model_id
          let foundDevice = null;
          for (const auditorium of payload.auditoriums || []) {
            foundDevice = (auditorium.devices || []).find(
              (d) =>
                d.serial_number === newDevice.serial_number &&
                d.device_model_id === newDevice.device_model_id,
            );
            if (foundDevice) break;
          }
          if (foundDevice) {
            return {
              ...newDevice,
              software_version:
                foundDevice.software_version || newDevice.software_version,
              is_certificate_locked:
                foundDevice.is_certificate_locked != null
                  ? foundDevice.is_certificate_locked
                  : newDevice.is_certificate_locked,
            };
          }
          return newDevice;
        });
      payload.standby_devices = [
        ...(payload.standby_devices || []),
        ...uniqueNewDevices,
      ];
    }

    dispatch(setDevices(payload));
    sendPostMessageToParentFrame(payload);
  };
}

export function deleteDevice({ device, screenId, suite }) {
  return (dispatch, getState) => {
    if (!device || !device.uuid) {
      console.error('deleteDevice called with invalid device data');
      return;
    }

    const facilityDevicesData = getState().facilityDevices.data;
    const payload = cloneDeep(facilityDevicesData);
    let ipUuidsToUnassign = [];

    if (screenId) {
      const screenIndex = payload.auditoriums.findIndex(
        (i) => i.screenId === screenId,
      );
      if (screenIndex !== -1) {
        const screenDevices = payload.auditoriums[screenIndex].devices || [];
        const { updatedDevices, deviceFound } = removeDeviceFromScreen(
          screenDevices,
          device.uuid,
          suite,
        );

        // If a device or suite was removed, collect IPs from the REMOVED device (not from the updated list)
        if (deviceFound) {
          // Find the removed device (unmapped or with the suite just removed)
          let removedDevice = null;
          if (!suite || suite === null) {
            // Unmapped: find device with no suites
            removedDevice = screenDevices.find(
              (d) =>
                d.uuid === device.uuid && (!d.suites || d.suites.length === 0),
            );
          } else {
            // Suite: find device with the suite (before removal)
            removedDevice = screenDevices.find(
              (d) =>
                d.uuid === device.uuid &&
                d.suites?.some((s) => s.suite_number === suite),
            );
          }
          if (removedDevice) {
            ipUuidsToUnassign = collectIpUuidsFromDevice(removedDevice, suite);
          }
          payload.auditoriums[screenIndex].devices = updatedDevices;
        }
      }
    } else {
      // Removing from standby
      payload.standby_devices = (payload.standby_devices || []).filter(
        (d) => d.uuid !== device.uuid,
      );
    }

    // Unassign collected IP UUIDs from the relevant screen
    if (screenId && ipUuidsToUnassign.length > 0) {
      const screen = payload.auditoriums.find((i) => i.screenId === screenId);
      if (screen) {
        screen.ip_details = (screen.ip_details || []).map((ip) =>
          ipUuidsToUnassign.includes(ip.uuid)
            ? { ...ip, is_assigned: false }
            : ip,
        );
      }
    }

    dispatch(setDevices(payload));
    sendPostMessageToParentFrame(payload);
  };
}

export function updateStandbyDevice({ device }) {
  return (dispatch, getState) => {
    if (!device || !device.uuid) {
      console.error(
        '[updateStandbyDevice] Called with invalid device data:',
        device,
      );
      return;
    }

    const facilityDevicesData = getState().facilityDevices.data;
    const payload = cloneDeep(facilityDevicesData);

    // Find the device in standby devices
    const deviceIndex = payload.standby_devices.findIndex(
      (d) => d.uuid === device.uuid,
    );

    if (deviceIndex !== -1) {
      // Update the device while preserving any fields that should be kept
      const existingDevice = payload.standby_devices[deviceIndex];
      payload.standby_devices[deviceIndex] = {
        ...existingDevice, // Keep existing fields
        ...device, // Apply new fields
        // Preserve critical fields if not provided in update
        suites: device.suites || existingDevice.suites || [],
        ip_uuid: device.ip_uuid || existingDevice.ip_uuid,
        device_ip: device.device_ip || existingDevice.device_ip,
        subnet_mask: device.subnet_mask || existingDevice.subnet_mask,
        gateway: device.gateway || existingDevice.gateway,
      };

      dispatch(setDevices(payload));
      sendPostMessageToParentFrame(payload);
    }
  };
}

export function updateDevice({ current, screenId, previous }) {
  return (dispatch, getState) => {
    if (!current?.uuid) return;

    const facilityDevicesData = getState().facilityDevices.data;
    const payload = cloneDeep(facilityDevicesData);

    if (!screenId) return;

    const screenIndex = payload.auditoriums.findIndex(
      (a) => a.screenId === screenId,
    );
    if (screenIndex === -1) return;

    const screen = payload.auditoriums[screenIndex];
    const screenDevices = screen.devices || [];

    // Match by UUID and (optional) suite number
    const deviceIndex = screenDevices.findIndex((d) => {
      const sameUuid = d.uuid === previous.uuid;
      const suiteNumber = current.suites?.[0]?.suite_number;
      return suiteNumber
        ? sameUuid && d.suites?.[0]?.suite_number === suiteNumber
        : sameUuid;
    });

    if (deviceIndex === -1) return;

    // Merge updated fields into existing device
    const existingDevice = screenDevices[deviceIndex];
    const updatedDevice = {
      ...existingDevice,
      ...current,
    };

    const currentSuite = current.suites?.[0]?.suite_number;
    const previousSuite = previous.suites?.[0]?.suite_number;

    const ipChanged = current.ip_uuid !== previous.ip_uuid;
    const suiteUnchanged = currentSuite === previousSuite;

    if (ipChanged && suiteUnchanged) {
      const { device: ipUpdatedDevice, screen: ipUpdatedScreen } =
        modifyIpForDevice({
          device: updatedDevice,
          ipUuid: current.ip_uuid,
          ipDetails: {
            device_ip: current.device_ip,
            subnet_mask: current.subnet_mask,
            gateway: current.gateway,
          },
          screen,
          suiteNumber: currentSuite,
        });

      screenDevices[deviceIndex] = ipUpdatedDevice;

      payload.auditoriums[screenIndex] = {
        ...screen,
        devices: screenDevices,
        ip_details: ipUpdatedScreen.ip_details,
      };
    } else {
      screenDevices[deviceIndex] = updatedDevice;

      payload.auditoriums[screenIndex] = {
        ...screen,
        devices: screenDevices,
      };
    }

    dispatch(setDevices(payload));
    sendPostMessageToParentFrame(payload);
  };
}

export function moveDeviceToScreen({
  device,
  fromScreenId,
  toScreenId,
  fromSuite,
}) {
  return (dispatch, getState) => {
    if (!device?.uuid) return;

    const facilityDevicesData = getState().facilityDevices.data;
    const payload = cloneDeep(facilityDevicesData);
    const auditoriums = payload.auditoriums;

    const sourceScreenIndex = fromScreenId
      ? auditoriums.findIndex((s) => s.screenId === fromScreenId)
      : -1;
    const targetScreenIndex = auditoriums.findIndex(
      (s) => s.screenId === toScreenId,
    );

    if (targetScreenIndex === -1) {
      console.error(
        `[moveDeviceToScreen] Target screen ${toScreenId} not found.`,
      );
      return;
    }

    const sourceScreen = auditoriums[sourceScreenIndex];
    const targetScreen = auditoriums[targetScreenIndex];

    const isSameScreen = fromScreenId === toScreenId;

    if (isSameScreen && sourceScreen && device.ip_uuid) {
      const { screen: updatedSourceScreen } = modifyIpForDevice({
        device,
        ipUuid: null,
        ipDetails: {},
        screen: sourceScreen,
        suiteNumber: fromSuite,
        isAssigning: false,
      });
      auditoriums[sourceScreenIndex] = updatedSourceScreen;
    }

    const exactDeviceIndex = sourceScreen?.devices.findIndex(
      (d) =>
        d.uuid === device.uuid &&
        d.suites?.some((s) => s.suite_number === fromSuite),
    );
    const isUnmappedAlready = sourceScreen?.devices?.some(
      (d) => d.uuid === device.uuid && d.suites.length === 0,
    );

    if (exactDeviceIndex === -1) return;

    const selectedDevice = sourceScreen.devices[exactDeviceIndex];

    if (isSameScreen) {
      if (!isUnmappedAlready) {
        selectedDevice.suites = [];
        selectedDevice.device_role =
          device.suites?.[0]?.device_role || device.device_role;
      } else {
        sourceScreen.devices.splice(exactDeviceIndex, 1);
      }
      auditoriums[sourceScreenIndex] = sourceScreen;
    } else {
      const alreadyInTarget = targetScreen.devices?.some(
        (d) => d.uuid === device.uuid && d.suites.length === 0,
      );

      const { screen: cleanedSourceScreen } = modifyIpForDevice({
        device,
        screen: sourceScreen,
        suiteNumber: fromSuite,
        isAssigning: false,
      });
      auditoriums[sourceScreenIndex] = cleanedSourceScreen;
      cleanedSourceScreen.devices.splice(exactDeviceIndex, 1);

      if (!alreadyInTarget) {
        const baseDevice = {
          ...device,
          device_role: device.suites?.[0]?.device_role || device.device_role,
          suites: [],
          is_unmapped: true,
          ip_uuid: null,
          device_ip: null,
          subnet_mask: null,
          gateway: null,
        };

        targetScreen.devices = targetScreen.devices || [];
        targetScreen.devices.push(baseDevice);
        targetScreen.devices.sort((a, b) =>
          (a.serial_number || '').localeCompare(b.serial_number || ''),
        );

        auditoriums[targetScreenIndex] = targetScreen;
      }
    }

    dispatch(setDevices(payload));
    sendPostMessageToParentFrame(payload);
  };
}

export function moveDeviceToStandby({ device, screenId, suite }) {
  return (dispatch, getState) => {
    if (!device || !device.uuid) return;
    const facilityDevicesData = getState().facilityDevices.data;
    const payload = cloneDeep(facilityDevicesData);

    const sourceScreenIndex = screenId
      ? payload.auditoriums.findIndex((i) => i.screenId === screenId)
      : -1;
    const sourceScreen =
      sourceScreenIndex !== -1 ? payload.auditoriums[sourceScreenIndex] : null;

    // Remove from source
    let movedDeviceData = device;
    if (sourceScreen) {
      const result = handleDeviceMovement({
        device,
        sourceScreen,
        targetScreen: null,
        suiteNumber: suite,
      });
      movedDeviceData = result?.movedDeviceData || device;
      payload.auditoriums[sourceScreenIndex] = sourceScreen;
    }

    // Check for duplicate in standby (serial_number + device_model_id)
    const standbyDevices = payload.standby_devices || [];
    const alreadyExists = standbyDevices.some(
      (d) =>
        d.serial_number === movedDeviceData.serial_number &&
        d.model_number === movedDeviceData.model_number,
    );

    if (alreadyExists) {
      // showErrorMsgBanner('Silent skip: Device already exists in standby.');
      dispatch(setDevices(payload));
      sendPostMessageToParentFrame(payload);
      return;
    }

    // Add to standby
    const standbyDevice = {
      ...movedDeviceData,
      suites: null,
      device_role: device.suites?.[0]?.device_role || device.device_role,
    };

    standbyDevices.push(standbyDevice);
    standbyDevices.sort((a, b) =>
      (a.serial_number || '').localeCompare(b.serial_number || ''),
    );

    payload.standby_devices = standbyDevices;

    dispatch(setDevices(payload));
    sendPostMessageToParentFrame(payload);
  };
}

export function updateDeviceMeta(device) {
  return (dispatch, getState) => {
    const facilityDevicesData = getState().facilityDevices.data;
    const payload = cloneDeep(facilityDevicesData);

    //in standby
    payload.standby_devices = (payload.standby_devices || []).map((d) =>
      d.uuid === device.uuid
        ? {
            ...d,
            software_version: device.software_version,
            is_certificate_locked: device.is_certificate_locked,
          }
        : d,
    );

    //in auditorium
    payload.auditoriums = payload.auditoriums.map((auditorium) => {
      const devicesToUpdate = (auditorium.devices || []).filter(
        (d) => d.uuid === device.uuid,
      );
      if (devicesToUpdate.length === 0) return auditorium;

      return {
        ...auditorium,
        devices: auditorium.devices.map((d) =>
          d.uuid === device.uuid
            ? {
                ...d,
                software_version: device.software_version,
                is_certificate_locked: device.is_certificate_locked,
              }
            : d,
        ),
      };
    });

    // Dispatch updates
    dispatch(setDevices(payload));
    sendPostMessageToParentFrame(payload);
  };
}

export function updateFacilityCertificates(data) {
  return (dispatch, getState) => {
    const facilityDevicesData = getState().facilityDevices.data;
    const payload = cloneDeep(facilityDevicesData);

    let deviceExists = false;
    payload.standby_devices = payload.standby_devices.map((device) => {
      if (
        device.device_model_id === data.device_model_id &&
        device.serial_number === data.serial_number
      ) {
        deviceExists = true;
        return {
          ...device,
          has_certificate: data.has_certificate,
          certificate: data.certificate,
        };
      }
      return device;
    });
    payload.auditoriums = payload.auditoriums.map((screen) => {
      const devices = screen.devices.map((device) => {
        if (
          device.device_model_id === data.device_model_id &&
          device.serial_number === data.serial_number
        ) {
          deviceExists = true;
          return {
            ...device,
            has_certificate: data.has_certificate,
            certificate: data.certificate,
          };
        }
        return device;
      });
      return { ...screen, devices };
    });
    if (!deviceExists) {
      payload.standby_devices = [
        ...payload.standby_devices,
        { ...data, uuid: uuidV4() },
      ];
    }

    dispatch(setDevices(payload));
    sendPostMessageToParentFrame(payload);
  };
}

export function handlePostMessages(data) {
  return (dispatch, getState) => {
    const facilityDevicesData = getState().facilityDevices.data;
    const payload = cloneDeep(facilityDevicesData);

    switch (data.type) {
      case 'edit_existing_screen': {
        const screenIndex = payload.auditoriums.findIndex(
          (i) => i.uuid === data.screen.screenUuid,
        );
        if (screenIndex !== -1) {
          payload.auditoriums[screenIndex] = {
            ...payload.auditoriums[screenIndex],
            number: Number(data.screen.screenNumber),
            name: data.screen.screenName,
            display_name: data.screen.screenName,
            is_active: data.screen.isActive === 'true',
          };
          dispatch(setDevices(payload));
          sendPostMessageToParentFrame(payload);
        }
        break;
      }
      case 'edit_new_screen': {
        const screenIndex = payload.auditoriums.findIndex(
          (i) => i.screenId === data.screen.screenId,
        );
        if (screenIndex !== -1) {
          payload.auditoriums[screenIndex] = {
            ...payload.auditoriums[screenIndex],
            number: Number(data.screen.screenNumber),
            name: data.screen.screenName,
            display_name: data.screen.screenName,
            is_active: data.screen.isActive === 'true',
          };
        } else {
          payload.auditoriums = [
            ...payload.auditoriums,
            {
              screenId: data.screen.screenId,
              number: Number(data.screen.screenNumber),
              name: data.screen.screenName,
              display_name: data.screen.screenName,
              devices: [],
              is_active: data.screen.isActive === 'true',
            },
          ];
        }
        dispatch(setDevices(payload));
        sendPostMessageToParentFrame(payload);
        break;
      }
      case 'remove_new_screen': {
        const initialLength = payload.auditoriums.length;
        payload.auditoriums = payload.auditoriums.filter(
          (i) => i.screenId !== data.screen.screenId,
        );
        if (payload.auditoriums.length < initialLength) {
          // Check if the clone was modified
          dispatch(setDevices(payload));
          sendPostMessageToParentFrame(payload);
        }
        break;
      }
      // this is where we setup our redux state for frontend
      case 'FACILITY_DEVICES': {
        const incomingPayload = data.payload;

        // process device suites
        const processDeviceSuites = (device, ipDetails) =>
          (device.suites || []).map((suite) => {
            const assignedIp = ipDetails.find(
              (ip) =>
                ip.device_id === device.uuid &&
                ip.suite_number === suite.suite_number,
            );
            return {
              ...suite,
              ...(assignedIp || {}),
              ip_uuid: assignedIp?.uuid,
            };
          });

        const processDeviceIpInfo = (device, ipDetails) => {
          const deviceUuid = device.uuid;

          const assignedIp = ipDetails.find((ip) => {
            if (ip.device_id !== deviceUuid) return false;

            // If device is unmapped (no suites or empty suites array)
            if (!Array.isArray(device.suites) || device.suites.length === 0) {
              return ip.suite_number === null || ip.suite_number === undefined;
            }

            // If device has suites, match any suite number
            return device.suites.some(
              (suite) => suite.suite_number === ip.suite_number,
            );
          });

          return assignedIp
            ? {
                ip_uuid: assignedIp.uuid,
                device_ip: assignedIp.device_ip,
                subnet_mask: assignedIp.subnet_mask,
                gateway: assignedIp.gateway,
              }
            : {};
        };

        const auditoriums = (incomingPayload.auditoriums || [])
          .map((sourceAuditorium) => {
            // Process IP details
            const ipDetails = (sourceAuditorium.ip_details || []).map((ip) => ({
              ...ip,
              is_assigned: !!ip.device_id,
            }));

            // Process devices
            const devices = (sourceAuditorium.devices || []).map((device) => ({
              ...device,
              uuid: device.uuid ?? uuidV4(),
              is_unmapped: !device.suites?.length,
              ...processDeviceIpInfo(device, ipDetails),
              suites: processDeviceSuites(device, ipDetails),
            }));

            return {
              ...sourceAuditorium,
              uuid: sourceAuditorium.uuid,
              screenId: sourceAuditorium.uuid ?? uuidV4(),
              devices,
              ip_details: ipDetails,
            };
          })
          .sort((a, b) => Number(a.number) - Number(b.number));

        const result = {
          ...incomingPayload,
          auditoriums,
        };

        dispatch(setDevices(result));
        break;
      }
      default:
        break;
    }
  };
}

/* -- IP Management Actions -- */
export function addIpAddress({ screenId, ipDetails }) {
  return (dispatch, getState) => {
    const facilityDevicesData = getState().facilityDevices.data;
    // Basic validation (can be enhanced)
    if (!ipDetails.device_ip) {
      dispatch(displayError(I18n.t('ipRequired')));
      return;
    }

    const payload = cloneDeep(facilityDevicesData);
    const newIpDetailsWithUuid = {
      uuid: ipDetails.uuid,
      device_ip: ipDetails.device_ip || null,
      subnet_mask: ipDetails.subnet_mask || null,
      gateway: ipDetails.gateway || null,
      is_assigned: false,
    };

    let ipAdded = false;
    let screenFound = false;
    payload.auditoriums = payload.auditoriums.map((auditorium) => {
      if (auditorium.screenId === screenId || auditorium.uuid === screenId) {
        screenFound = true;
        const currentIpDetails = auditorium.ip_details || [];
        ipAdded = true; // Mark that we intend to add
        return {
          ...auditorium,
          ip_details: [...currentIpDetails, newIpDetailsWithUuid],
        };
      }
      return auditorium;
    });

    if (!screenFound) {
      console.error(`[addIpAddress] Screen with ID ${screenId} not found.`);
      return;
    }

    // Only dispatch if an IP was actually added
    if (ipAdded) {
      dispatch(setDevices(payload));
      sendPostMessageToParentFrame(payload);
    }

    const currentScreenDevice =
      getState().facilityDevices.data.auditoriums?.find(
        (a) => a.screenId === screenId,
      );
    const currentIpDetails = currentScreenDevice?.ip_details;

    return { newIP: newIpDetailsWithUuid, currentIpDetails };
  };
}

export function updateIpAddress({ screenId, ipUuid, newIpDetails }) {
  return (dispatch, getState) => {
    // Basic validation
    if (!ipUuid) {
      return;
    }

    const facilityDevicesData = getState().facilityDevices.data;
    const payload = cloneDeep(facilityDevicesData);
    let ipUpdated = false;
    let _screenFound = false;

    payload.auditoriums = payload.auditoriums.map((auditorium) => {
      if (auditorium.screenId === screenId || auditorium.uuid === screenId) {
        _screenFound = true;
        const currentIpDetails = auditorium.ip_details || [];

        // 1. Update IP in ip_details
        const updatedIpDetailsArray = currentIpDetails.map((ip) => {
          if (ip.uuid === ipUuid) {
            ipUpdated = true;
            return {
              ...ip,
              device_ip: newIpDetails.device_ip,
              subnet_mask: newIpDetails.subnet_mask || null,
              gateway: newIpDetails.gateway || null,
            };
          }
          return ip;
        });

        if (!ipUpdated) {
          return auditorium;
        }

        // 2. Update any device assigned this IP
        const updatedDevicesArray = (auditorium.devices || []).map((device) => {
          let updatedDevice = { ...device };

          // Check top-level IP
          if (updatedDevice.ip_uuid === ipUuid) {
            updatedDevice.device_ip = newIpDetails.device_ip;
            updatedDevice.subnet_mask = newIpDetails.subnet_mask || null;
            updatedDevice.gateway = newIpDetails.gateway || null;
            // deviceWasModified = true;
          }

          // Check suites IP, can be removed once confirmed
          if (updatedDevice.suites && updatedDevice.suites.length > 0) {
            const originalSuites = updatedDevice.suites;
            const updatedSuites = originalSuites.map((suite) => {
              if (suite.ip_uuid === ipUuid) {
                return {
                  ...suite,
                  device_ip: newIpDetails.device_ip,
                  subnet_mask: newIpDetails.subnet_mask || null,
                  gateway: newIpDetails.gateway || null,
                };
              }
              return suite;
            });

            // Only update suites on the device if the suites array content actually changed
            if (
              updatedSuites.some(
                (suite, index) => suite !== originalSuites[index],
              )
            ) {
              updatedDevice.suites = updatedSuites;
            }
          }

          return updatedDevice;
        });

        return {
          ...auditorium,
          ip_details: updatedIpDetailsArray,
          devices: updatedDevicesArray,
        };
      }
      return auditorium;
    });

    // Only dispatch if an IP was actually updated
    if (ipUpdated) {
      dispatch(setDevices(payload));
      sendPostMessageToParentFrame(payload);
    }
  };
}

export function deleteIpAddress({ screenId, ipUuid }) {
  return (dispatch, getState) => {
    // Basic validation
    if (!ipUuid) {
      console.error(`[deleteIpAddress] Missing ipUuid parameter.`);
      return;
    }

    const facilityDevicesData = getState().facilityDevices.data;
    const payload = cloneDeep(facilityDevicesData);
    let ipRemoved = false;
    let _screenFound = false;

    payload.auditoriums = payload.auditoriums.map((auditorium) => {
      if (auditorium.screenId === screenId || auditorium.uuid === screenId) {
        _screenFound = true;
        const originalIpDetails = auditorium.ip_details || [];

        // 1. Filter the IP from ip_details using ipUuid
        const updatedIpDetailsArray = originalIpDetails.filter(
          (ip) => ip.uuid !== ipUuid,
        );

        if (updatedIpDetailsArray.length < originalIpDetails.length) {
          ipRemoved = true;
        }

        // 2. Find any device assigned this IP and nullify its fields
        const updatedDevicesArray = (auditorium.devices || []).map((device) => {
          let updatedDevice = { ...device };

          // Check top-level IP
          if (updatedDevice.ip_uuid === ipUuid) {
            updatedDevice.device_ip = null;
            updatedDevice.subnet_mask = null;
            updatedDevice.gateway = null;
            updatedDevice.ip_uuid = null;
          }

          // Check suites IP
          if (updatedDevice.suites && updatedDevice.suites.length > 0) {
            const updatedSuites = updatedDevice.suites.map((suite) => {
              if (suite.ip_uuid === ipUuid) {
                return {
                  ...suite,
                  device_ip: null,
                  subnet_mask: null,
                  gateway: null,
                  ip_uuid: null,
                };
              }
              return suite;
            });

            // Only update suites on the device if the suites array content actually changed
            if (
              updatedSuites.some(
                (suite, index) => suite !== updatedDevice.suites[index],
              )
            ) {
              updatedDevice.suites = updatedSuites;
            }
          }

          return updatedDevice;
        });

        return {
          ...auditorium,
          ip_details: updatedIpDetailsArray,
          devices: updatedDevicesArray,
        };
      }
      return auditorium;
    });

    // Only dispatch if an IP was actually removed
    if (ipRemoved) {
      dispatch(setDevices(payload));
      sendPostMessageToParentFrame(payload);
    }
  };
}

/* -- Reducers -- */
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_DEVICES: {
      const result = {};
      if (!isEmpty(action.payload)) {
        result.data = { $set: action.payload };
      }
      return update(state, result);
    }
    case SET_LOADING:
      return update(state, { loading: { $set: action.payload } });
    case SET_ERROR:
      return update(state, { error: { $set: true }, loading: { $set: false } });
    case SET_DEVICE_MODELS:
      return update(state, { deviceModels: { $set: action.payload } });
    case SET_PAGE_PARAMS:
      return update(state, {
        pageParams: { [action.payload.key]: { $set: action.payload.value } },
      });
    default:
      return state;
  }
}
